import type { Build } from './types.ts';

/**
 * #GR90 — resumable action affordances. Several admin flows (brand publish, library
 * maintain, first-launch go-live, retire) tracked their in-flight / ready / publishable
 * step in click-time `useState`, so any remount (tab switch, navigate to check the preview,
 * reload, modal-close/reopen) reset them and the resume/publish affordance + its status chip
 * vanished — though the backend work persisted and the change was safely committed. Same class
 * as #GR47/#GR19/#GR23: DERIVE the flow state from persisted `builds` rows, not click-time state.
 *
 * These are PURE (no React/supabase) so the derivation is unit-tested; components use
 * `optimisticClickState ?? derived` — instant on click, recovered on remount. FE-only; no
 * endpoint, no write path, no security surface (the server gates are unchanged).
 *
 * Job-type map (openBuild jobType): brand-relock → 'assembly' (brand.ts deploy on app/<slug>);
 * run-build brand lock → 'assembly'; build chat → 'build-chat'; maintain-apply → 'maintain'
 * (regen on app/<slug>); maintain-golive / go-live / promote → 'promote'; retire-client → 'retire'.
 */

const ts = (b: { created_at: string }): number => new Date(b.created_at).getTime();
const inFlight = (b: Build): boolean => b.outcome === 'queued' || b.outcome === 'running';

/** Newest build (by created_at) matching pred, independent of input order. null if none. */
function newest(builds: Build[], pred: (b: Build) => boolean): Build | null {
  let best: Build | null = null;
  for (const b of builds) if (pred(b) && (best === null || ts(b) > ts(best))) best = b;
  return best;
}

/** The newest in-flight (queued/running) build of a job type — the resumable status chip. */
export function inFlightBuildId(builds: Build[], jobType: string): string | null {
  const b = newest(builds, (x) => x.job_type === jobType);
  return b && inFlight(b) ? b.id : null;
}

export interface BrandPublishResume {
  /** The brand preview (assembly) build to show the preview chip for. */
  relockBuildId: string | null;
  /** An in-flight publish (promote) of that preview to track, else null. */
  goliveBuildId: string | null;
  /** Render the admin-live brand publish block? (an unpublished relock, or its publish running) */
  show: boolean;
}

/**
 * #GR90 #1 — the Wizard admin-live brand publish affordance (go-live-protection §4–5 / #GR88).
 * A relock commits an `assembly` deploy to app/<slug>; publishing promotes it. There's something
 * to publish when the newest successful assembly is NEWER than the last publish (promote) — a
 * relock happened since. While that publish runs, track its promote row instead. Once the promote
 * completes (newest), nothing is unpublished → the block hides (the brand is live).
 */
export function brandPublishResume(builds: Build[]): BrandPublishResume {
  const latestAssembly = newest(builds, (b) => b.job_type === 'assembly' && b.outcome === 'success' && !!b.commit_sha);
  if (!latestAssembly) return { relockBuildId: null, goliveBuildId: null, show: false };
  const latestPromote = newest(builds, (b) => b.job_type === 'promote');
  const unpublished = !latestPromote || ts(latestAssembly) > ts(latestPromote);
  const publishInFlight = !!latestPromote && inFlight(latestPromote) && ts(latestPromote) > ts(latestAssembly);
  return {
    relockBuildId: latestAssembly.id,
    goliveBuildId: publishInFlight ? latestPromote!.id : null,
    show: unpublished || publishInFlight,
  };
}

export interface MaintainResume {
  /** The regen (maintain) build whose green preview gates Go-live. */
  applyBuildId: string | null;
  /** An in-flight publish (promote) of that update to track, else null. */
  goliveBuildId: string | null;
  /** There's a resumable maintain flow (apply running, green-unpublished, or publish running). */
  active: boolean;
}

/**
 * #GR90 #2 — the FleetView MaintainClient flow (maintain-flow.md §5). maintain-apply bumps pins +
 * regens on app/<slug> (a 'maintain' build → green preview); maintain-golive promotes it. The flow
 * is resumable while: the apply is running, OR it succeeded but hasn't been published (newest
 * maintain is newer than the last promote), OR its publish is running. So reopening the modal (or a
 * reload) re-derives applyBuildId/goliveBuildId and the Go-live button survives — making the
 * "Safe to leave this page — it keeps running" copy actually true.
 */
export function maintainResume(builds: Build[]): MaintainResume {
  const latestMaintain = newest(builds, (b) => b.job_type === 'maintain');
  if (!latestMaintain) return { applyBuildId: null, goliveBuildId: null, active: false };
  const latestPromote = newest(builds, (b) => b.job_type === 'promote');
  const applyRunning = inFlight(latestMaintain);
  const greenUnpublished =
    latestMaintain.outcome === 'success' && (!latestPromote || ts(latestMaintain) > ts(latestPromote));
  const publishInFlight = !!latestPromote && inFlight(latestPromote) && ts(latestPromote) > ts(latestMaintain);
  const active = applyRunning || greenUnpublished || publishInFlight;
  return {
    applyBuildId: active ? latestMaintain.id : null,
    goliveBuildId: publishInFlight ? latestPromote!.id : null,
    active,
  };
}

/**
 * Model-A maintenance re-publish — "Publish update" button gating (go-live-protection §4). An
 * already-LIVE, maintainable client with a FRESH approval (a hand-build content update) needs a way
 * to re-publish; the wizard's first-go-live button renders only for !is_live, so a live client
 * otherwise has no publish button. "Fresh" = the approval timestamp is newer than the last publish
 * (the newest promote's promoted_at ?? created_at) — so the button is OFF once nothing's pending and
 * ON right after a new approval. An in-flight promote keeps it ON so the button/chip survives the
 * publish. PURE so the gate is unit-tested; the component composes it with its live/admin flags.
 */
export function approvalNewerThanLastPublish(approvalAt: string | null | undefined, builds: Build[]): boolean {
  if (!approvalAt) return false;
  const lastPromote = newest(builds, (b) => !!b.promote_commit_sha);
  const lastPublishAt = lastPromote ? (lastPromote.promoted_at ?? lastPromote.created_at) : null;
  return !lastPublishAt || new Date(approvalAt).getTime() > new Date(lastPublishAt).getTime();
}

export interface PublishUpdateOpts {
  isLive: boolean;
  /** isAdmin && isMaintainableSlug(slug) — the frontend mirror of the backend allow-condition. */
  maintainLive: boolean;
  clientApproved: boolean;
  approvalAt: string | null | undefined;
  builds: Build[];
  /** A promote is in flight (effSendLiveBuildId present) — keeps the button/chip up during publish. */
  promoteInFlight: boolean;
}

/** Whether to show the live-client "Publish update" button (reuses the go-live re-publish path). */
export function canPublishUpdate(o: PublishUpdateOpts): boolean {
  return (
    o.isLive &&
    o.maintainLive &&
    o.clientApproved &&
    (approvalNewerThanLastPublish(o.approvalAt, o.builds) || o.promoteInFlight)
  );
}
